import { mcpServerMatches } from "../../lib/mcp-catalog";
import { routeToHash } from "../../lib/routing";
import { onSpaLinkClick } from "../../lib/link-click";
import { useMcpServers } from "../../lib/use-mcp-servers";
import styles from "./McpCrossSearch.module.css";

interface Props {
  /** The Skills & Plugins search text. */
  query: string;
  /** Opens MCP Servers with this text in its search box. */
  onOpen: (query: string) => void;
}

/**
 * "Looking for a connection to a system?" (#939, Feature I): a Skills & Plugins search can miss a
 * matching MCP server, so when one does match this points to MCP Servers with the search carried
 * over. It uses the catalog's own matching (title, server name and description, ignoring case) on
 * the shared server list. It shows nothing while the list loads, when it can't be loaded, or when
 * nothing matches, so a search reads as it did without it. The list is read only once someone has
 * searched, because this is rendered only then.
 */
/** Longest search text shown in the line; the count and the link use the whole search. */
const MAX_SHOWN = 40;

export default function McpCrossSearch({ query, onOpen }: Props) {
  const servers = useMcpServers();
  const text = query.trim();
  if (!text || servers.status !== "ready") return null;

  const count = servers.result.servers.filter((s) => mcpServerMatches(s, text)).length;
  if (count === 0) return null;
  const shown = text.length > MAX_SHOWN ? `${text.slice(0, MAX_SHOWN).trimEnd()}…` : text;

  return (
    <p className={styles.pointer}>
      Looking for a connection to a system?{" "}
      <a
        href={routeToHash({ page: "mcp", filterQuery: text })}
        onClick={onSpaLinkClick(() => onOpen(text))}
        className={styles.link}
      >
        {count} MCP {count === 1 ? "server matches" : "servers match"} &lsquo;{shown}&rsquo; &rarr;
      </a>
    </p>
  );
}
